import { Button, Modal } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import ComponentEditableList from '../../components/ComponentEditableList'
import { useState, useEffect } from 'react'
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function ShopifyVariantBundleEdit({
    product,
    variant,
    fetchProduct,
    sortedComponents,
}) {
    const [opened, { open, close }] = useDisclosure(false)

    const initialChildren = variant.components.map((child) => ({
        quantity: child.pivot.quantity,
        component_id: child.id,
        full_name: child.component_category.name_fr + ' — ' + child.name,
    }))

    const [children, setChildren] = useState(initialChildren)
    const [isSaving, setIsSaving] = useState(false)
    const { requestPost } = useAxiosApi()

    const childrenHandlers = {
        setState: setChildren,
        append: (newChild) => setChildren((prev) => [...prev, newChild]),
        remove: (index) => {
            setChildren((prev) => prev.filter((_, i) => i !== index))
        },
        reorder: ({ from, to }) => {
            setChildren((prev) => {
                const newChildren = [...prev]
                const [removed] = newChildren.splice(from, 1)
                newChildren.splice(to, 0, removed)
                return newChildren
            })
        },
        setItemProp: (index, prop, value) => {
            setChildren((prev) => {
                const newChildren = [...prev]
                newChildren[index] = { ...newChildren[index], [prop]: value }
                return newChildren
            })
        },
    }

    const saveChildren = async () => {
        setIsSaving(true)

        await requestPost(`/shopify/variants/${variant.id}/bundle`, {
            components: children.map((c) => ({
                component_id: c.component_id,
                quantity: c.quantity,
            })),
        })

        await fetchProduct()
        close()
    }

    useEffect(() => {
        if (opened === true) {
            setIsSaving(false)
            setChildren(initialChildren)
        }
    }, [opened])

    const childrenIds = children.map((c) => c.component_id)
    const sortedComponentsWithoutChildren = sortedComponents.filter(
        (c) => !childrenIds.includes(c.component.id)
    )

    return (
        <>
            <Modal
                opened={opened}
                position="right"
                onClose={close}
                size="xl"
                title={'Bundle : ' + product.title + ' - ' + variant.title}
            >
                <div className="flex flex-col text-gray-500 text-sm mb-5">
                    <span>
                        Le stock en vente des éléments de bundle détermine la
                        quantité disponible sur la variante Shopify.
                    </span>
                </div>

                <div className="flex flex-col min-h-[400px]">
                    <div className="flex flex-col flex-1">
                        <ComponentEditableList
                            children={children}
                            childrenHandlers={childrenHandlers}
                        />

                        {sortedComponentsWithoutChildren.length > 0 && (
                            <div className="flex flex-col gap-2 mb-5">
                                <span className="text-sm">
                                    Composants suggérés
                                </span>

                                <div className="flex flex-col gap-2">
                                    {sortedComponentsWithoutChildren.map(
                                        (c) => {
                                            const component = c.component
                                            const full_name = `${component.component_category.name_fr} — ${component.name}`

                                            return (
                                                <div
                                                    className="flex w-full justify-between items-center"
                                                    key={component.id}
                                                >
                                                    <span>{full_name}</span>

                                                    <Button
                                                        size="xs"
                                                        variant="default"
                                                        onClick={() =>
                                                            childrenHandlers.append(
                                                                {
                                                                    component_id:
                                                                        component.id,
                                                                    full_name,
                                                                    quantity: 1,
                                                                }
                                                            )
                                                        }
                                                    >
                                                        Ajouter
                                                    </Button>
                                                </div>
                                            )
                                        }
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2 ml-auto">
                        <Button onClick={close} variant="default">
                            Annuler
                        </Button>

                        <Button onClick={saveChildren} loading={isSaving}>
                            Sauvegarder les modifications
                        </Button>
                    </div>
                </div>
            </Modal>

            <Button variant="default" onClick={open} className="mt-2 mr-auto">
                Modifier le bundle
            </Button>
        </>
    )
}
